import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Search, MapPin, Stethoscope, Calendar as CalendarIcon, Clock, Navigation } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format, getDay } from 'date-fns';
import { getErrorMessage } from '@/lib/utils';


const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_BASE}/api`;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// All medical specializations
const ALL_SPECIALIZATIONS = [
  'Allergy and Immunology',
  'Anesthesiology',
  'Cardiology',
  'Cardiovascular Surgery',
  'Critical Care Medicine',
  'Dentistry',
  'Dermatology',
  'Diabetology',
  'Emergency Medicine',
  'Endocrinology',
  'ENT (Otolaryngology)',
  'Family Medicine',
  'Gastroenterology',
  'General Medicine',
  'General Surgery',
  'Geriatrics',
  'Gynecology',
  'Hematology',
  'Hepatology',
  'Infectious Disease',
  'Internal Medicine',
  'Interventional Radiology',
  'Neonatology',
  'Nephrology',
  'Neurology',
  'Neurosurgery',
  'Obstetrics',
  'Obstetrics and Gynecology',
  'Oncology',
  'Ophthalmology',
  'Oral and Maxillofacial Surgery',
  'Orthopedics',
  'Pain Management',
  'Pathology',
  'Pediatrics',
  'Pediatric Surgery',
  'Physical Medicine and Rehabilitation',
  'Plastic Surgery',
  'Podiatry',
  'Psychiatry',
  'Pulmonology',
  'Radiology',
  'Rheumatology',
  'Sports Medicine',
  'Thoracic Surgery',
  'Urology',
  'Vascular Surgery',
  'Ayurveda',
  'Homeopathy',
  'Unani',
  'Naturopathy',
  'Physiotherapy'
];

// All major Indian cities
const ALL_CITIES = [
  'Agra',
  'Ahmedabad',
  'Ajmer',
  'Aligarh',
  'Allahabad',
  'Amritsar',
  'Aurangabad',
  'Bangalore',
  'Bareilly',
  'Belgaum',
  'Bhavnagar',
  'Bhilai',
  'Bhopal',
  'Bhubaneswar',
  'Bikaner',
  'Chandigarh',
  'Chennai',
  'Coimbatore',
  'Cuttack',
  'Dehradun',
  'Delhi',
  'Dhanbad',
  'Durgapur',
  'Erode',
  'Faridabad',
  'Firozabad',
  'Ghaziabad',
  'Gorakhpur',
  'Guntur',
  'Gurgaon',
  'Guwahati',
  'Gwalior',
  'Hubli',
  'Hyderabad',
  'Indore',
  'Jabalpur',
  'Jaipur',
  'Jalandhar',
  'Jammu',
  'Jamnagar',
  'Jamshedpur',
  'Jhansi',
  'Jodhpur',
  'Kanpur',
  'Kochi',
  'Kolhapur',
  'Kolkata',
  'Kota',
  'Kozhikode',
  'Lucknow',
  'Ludhiana',
  'Madurai',
  'Mangalore',
  'Mathura',
  'Meerut',
  'Moradabad',
  'Mumbai',
  'Mysore',
  'Nagpur',
  'Nanded',
  'Nashik',
  'Navi Mumbai',
  'Noida',
  'Patna',
  'Pimpri-Chinchwad',
  'Pondicherry',
  'Pune',
  'Raipur',
  'Rajkot',
  'Ranchi',
  'Salem',
  'Sangli',
  'Siliguri',
  'Solapur',
  'Srinagar',
  'Surat',
  'Thane',
  'Thiruvananthapuram',
  'Tiruchirappalli',
  'Tiruppur',
  'Udaipur',
  'Ujjain',
  'Vadodara',
  'Varanasi',
  'Vasai-Virar',
  'Vijayawada',
  'Visakhapatnam',
  'Warangal'
];

export default function DoctorSearchComponent() {
  const { token, user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  const [doctorsAvailability, setDoctorsAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [doctorTimeSlots, setDoctorTimeSlots] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [locatingNearby, setLocatingNearby] = useState(false);
  const [bookingData, setBookingData] = useState({
    time_slot: '',
    notes: '',
  });

  // Handle "Near Me" button click
  const handleNearMe = () => {
    if (user?.city) {
      // Use MR's city from profile
      setCityFilter(user.city);
      toast.success(`Showing doctors in ${user.city}`);
    } else {
      // Try to get location from browser
      setLocatingNearby(true);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              // Use reverse geocoding to get city name
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );
              const data = await response.json();
              const city = data.address?.city || data.address?.town || data.address?.district || data.address?.state_district;
              
              if (city) {
                // Check if city is in our list
                const matchedCity = ALL_CITIES.find(c => 
                  c.toLowerCase() === city.toLowerCase() ||
                  city.toLowerCase().includes(c.toLowerCase()) ||
                  c.toLowerCase().includes(city.toLowerCase())
                );
                
                if (matchedCity) {
                  setCityFilter(matchedCity);
                  toast.success(`Showing doctors in ${matchedCity}`);
                } else {
                  toast.info(`${city} not in our city list. Please select manually.`);
                }
              } else {
                toast.error('Could not determine your city. Please select manually.');
              }
            } catch (error) {
              toast.error('Could not get location. Please select city manually.');
            } finally {
              setLocatingNearby(false);
            }
          },
          (error) => {
            setLocatingNearby(false);
            if (error.code === error.PERMISSION_DENIED) {
              toast.error('Location access denied. Please enable location or select city manually.');
            } else {
              toast.error('Could not get location. Please select city manually.');
            }
          },
          { timeout: 10000 }
        );
      } else {
        setLocatingNearby(false);
        toast.error('Location not supported. Please select city manually.');
      }
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    let filtered = doctors;
    
    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(
        (doc) =>
          doc.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.specialization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doc.city?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply specialization filter
    if (specializationFilter !== 'all') {
      filtered = filtered.filter(doc => doc.specialization === specializationFilter);
    }
    
    // Apply city filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(doc => doc.city === cityFilter);
    }
    
    setFilteredDoctors(filtered);
  }, [searchTerm, specializationFilter, cityFilter, doctors]);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${API}/doctors/search`);
      setDoctors(response.data);
      setFilteredDoctors(response.data);
      
      // Fetch availability for all doctors
      const availabilityPromises = response.data.map(async (doctor) => {
        try {
          const slotsResponse = await axios.get(`${API}/time-slots/doctor/${doctor.id}`);
          return { doctorId: doctor.id, slots: slotsResponse.data };
        } catch (error) {
          return { doctorId: doctor.id, slots: [] };
        }
      });
      
      const availabilities = await Promise.all(availabilityPromises);
      const availabilityMap = {};
      availabilities.forEach(({ doctorId, slots }) => {
        availabilityMap[doctorId] = slots;
      });
      setDoctorsAvailability(availabilityMap);
    } catch (error) {
      toast.error('Failed to fetch doctors');
    } finally {
      setLoading(false);
    }
  };

  const handleBookAppointment = async (doctor) => {
    setSelectedDoctor(doctor);
    
    // Fetch doctor's time slots
    try {
      const response = await axios.get(`${API}/time-slots/doctor/${doctor.id}`);
      setDoctorTimeSlots(response.data);
      setIsBookingOpen(true);
    } catch (error) {
      toast.error('Failed to fetch doctor availability');
    }
  };

  const handleViewProfile = async (doctor) => {
    setSelectedDoctor(doctor);
    
    // Fetch doctor's time slots for profile view
    try {
      const response = await axios.get(`${API}/time-slots/doctor/${doctor.id}`);
      setDoctorTimeSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch time slots');
    }
    
    setIsProfileOpen(true);
  };

  // Update available slots when date changes
  useEffect(() => {
    if (selectedDate && doctorTimeSlots.length > 0) {
      const dayName = DAYS[getDay(selectedDate)];
      const slotsForDay = doctorTimeSlots.filter(slot => slot.day_of_week === dayName);
      setAvailableSlots(slotsForDay);
      setBookingData(prev => ({ ...prev, time_slot: '' }));
    }
  }, [selectedDate, doctorTimeSlots]);

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    if (!bookingData.time_slot) {
      toast.error('Please select a time slot');
      return;
    }

    try {
      await axios.post(
        `${API}/appointments`,
        {
          doctor_id: selectedDoctor.id,
          appointment_date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: bookingData.time_slot,
          notes: bookingData.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Appointment booked successfully! SMS notification sent.');
      setIsBookingOpen(false);
      setSelectedDoctor(null);
      setSelectedDate(null);
      setDoctorTimeSlots([]);
      setAvailableSlots([]);
      setBookingData({ time_slot: '', notes: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to book appointment'));
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="overflow-x-hidden">
      <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
        {/* Search Input with Near Me Button */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              data-testid="doctor-search-input"
              type="text"
              placeholder="Search doctors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 sm:h-12 text-sm sm:text-base"
            />
          </div>
          <Button
            data-testid="near-me-btn"
            onClick={handleNearMe}
            disabled={locatingNearby}
            variant="outline"
            className="h-10 sm:h-12 px-3 sm:px-4 border-primary text-primary hover:bg-primary hover:text-white transition-colors"
          >
            <Navigation className={`h-4 w-4 sm:mr-2 ${locatingNearby ? 'animate-pulse' : ''}`} />
            <span className="hidden sm:inline">{locatingNearby ? 'Locating...' : 'Near Me'}</span>
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          <Select value={specializationFilter} onValueChange={setSpecializationFilter}>
            <SelectTrigger data-testid="specialization-filter" className="h-10 sm:h-11 text-xs sm:text-sm">
              <SelectValue placeholder="Specialization" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All Specializations</SelectItem>
              {ALL_SPECIALIZATIONS.map(spec => (
                <SelectItem key={spec} value={spec}>
                  {spec}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger data-testid="city-filter" className="h-10 sm:h-11 text-xs sm:text-sm">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All Cities</SelectItem>
              {ALL_CITIES.map(city => (
                <SelectItem key={city} value={city}>
                  {city}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filteredDoctors.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 sm:p-12 text-center">
          <p className="text-slate-600 text-sm sm:text-base">No doctors found matching your search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {filteredDoctors.map((doctor) => (
            <div
              key={doctor.id}
              className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 hover:shadow-md transition-all"
              data-testid={`doctor-card-${doctor.id}`}
            >
              {doctor.doctor_photo && (
                <div className="flex justify-center mb-3 sm:mb-4">
                  <img
                    src={doctor.doctor_photo}
                    alt={doctor.full_name}
                    className="h-16 w-16 sm:h-24 sm:w-24 rounded-full object-cover border-2 border-primary/20"
                  />
                </div>
              )}
              
              <div className="mb-3 sm:mb-4">
                <h3 className="font-heading font-bold text-base sm:text-xl text-background-dark mb-1 sm:mb-2">
                  {doctor.full_name}
                </h3>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-primary mb-1 sm:mb-2">
                  <Stethoscope className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="font-medium">{doctor.specialization}</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 mb-1 sm:mb-2">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{doctor.city}</span>
                </div>
                {doctor.clinic_name && (
                  <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3">{doctor.clinic_name}</p>
                )}
                
                {/* Availability Preview */}
                {doctorsAvailability[doctor.id] && doctorsAvailability[doctor.id].length > 0 && (
                  <div className="bg-accent/10 rounded-lg p-2 mt-2 sm:mt-3">
                    <div className="flex items-center gap-1 text-xs text-accent font-medium mb-1">
                      <Clock className="h-3 w-3" />
                      Available:
                    </div>
                    <div className="text-xs text-slate-700 space-y-0.5">
                      {doctorsAvailability[doctor.id].slice(0, 2).map((slot) => (
                        <div key={slot.id}>
                          {slot.day_of_week}s: {slot.start_time}-{slot.end_time}
                        </div>
                      ))}
                      {doctorsAvailability[doctor.id].length > 2 && (
                        <div className="text-primary font-medium">
                          +{doctorsAvailability[doctor.id].length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-3 sm:mb-4 text-xs sm:text-sm text-slate-600">
                <p className="line-clamp-2 mb-1 sm:mb-2">{doctor.clinic_address}</p>
                {doctor.clinic_address && (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor.clinic_address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline text-xs"
                  >
                    <MapPin className="h-3 w-3" />
                    View on Maps
                  </a>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  data-testid={`view-profile-${doctor.id}`}
                  onClick={() => handleViewProfile(doctor)}
                  variant="outline"
                  size="sm"
                  className="flex-1 rounded-full border-primary text-primary hover:bg-primary/10 text-xs sm:text-sm h-8 sm:h-9"
                >
                  Profile
                </Button>
                <Button
                  data-testid={`book-appointment-${doctor.id}`}
                  onClick={() => handleBookAppointment(doctor)}
                  size="sm"
                  className="flex-1 rounded-full bg-primary hover:bg-primary-hover text-xs sm:text-sm h-8 sm:h-9"
                >
                  <CalendarIcon className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  Book
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Doctor Profile Dialog */}
      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto mx-2 sm:mx-auto">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">{selectedDoctor?.full_name}'s Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 sm:space-y-6 mt-2 sm:mt-4">
            {selectedDoctor?.doctor_photo && (
              <div className="flex justify-center">
                <img
                  src={selectedDoctor.doctor_photo}
                  alt={selectedDoctor.full_name}
                  className="h-24 w-24 sm:h-40 sm:w-40 rounded-full object-cover border-4 border-primary/20"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:gap-6">
              <div>
                <Label className="text-slate-600 text-xs sm:text-sm">Specialization</Label>
                <p className="mt-1 text-sm sm:text-lg font-semibold text-primary">{selectedDoctor?.specialization}</p>
              </div>
              <div>
                <Label className="text-slate-600 text-xs sm:text-sm">City</Label>
                <p className="mt-1 text-sm sm:text-lg font-medium text-slate-900">{selectedDoctor?.city}</p>
              </div>
            </div>

            <div>
              <Label className="text-slate-600 text-xs sm:text-sm">Clinic Name</Label>
              <p className="mt-1 text-sm sm:text-lg font-medium text-slate-900">{selectedDoctor?.clinic_name}</p>
            </div>

            <div>
              <Label className="text-slate-600">Clinic Address</Label>
              <p className="mt-1 text-slate-900">{selectedDoctor?.clinic_address}</p>
              {selectedDoctor?.clinic_address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedDoctor.clinic_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm font-medium"
                >
                  <MapPin className="h-4 w-4" />
                  Open in Google Maps
                </a>
              )}
            </div>

            <div>
              <Label className="text-slate-600">Contact</Label>
              <p className="mt-1 text-slate-900">{selectedDoctor?.phone}</p>
            </div>

            <div>
              <Label className="text-slate-600">Email</Label>
              <p className="mt-1 text-slate-900">{selectedDoctor?.email}</p>
            </div>

            {doctorTimeSlots.length > 0 && (
              <div>
                <Label className="text-slate-600 mb-3 block">Weekly Availability Schedule</Label>
                <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 space-y-2">
                  {doctorTimeSlots.map((slot) => (
                    <div key={slot.id} className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4 text-accent" />
                      <span className="font-semibold text-slate-900">Every {slot.day_of_week}:</span>
                      <span className="text-slate-700">{slot.start_time} - {slot.end_time}</span>
                      <span className="text-xs text-slate-500">(Max {slot.max_appointments} appointments)</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  💡 This doctor is available at these times every week. Select a matching date when booking.
                </p>
              </div>
            )}

            {selectedDoctor?.visiting_card && (
              <div>
                <Label className="text-slate-600 mb-3 block">Visiting Card</Label>
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  <img
                    src={selectedDoctor.visiting_card}
                    alt="Doctor's visiting card"
                    className="w-full max-h-96 object-contain"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsProfileOpen(false)}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setIsProfileOpen(false);
                  handleBookAppointment(selectedDoctor);
                }}
                className="flex-1 rounded-full bg-primary hover:bg-primary-hover"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                Book Appointment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Dialog */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Appointment with {selectedDoctor?.full_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitBooking} className="space-y-4 mt-4">
            <div className="mb-6">
              <Label>Select Date</Label>
              <div className="mt-2 p-4 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                <p className="text-sm font-medium text-blue-900 mb-2">Doctor's Available Schedule:</p>
                <div className="space-y-1">
                  {doctorTimeSlots.map((slot) => (
                    <p key={slot.id} className="text-sm text-blue-800">
                      • Every <span className="font-semibold">{slot.day_of_week}</span>: {slot.start_time} - {slot.end_time}
                    </p>
                  ))}
                </div>
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date()}
                className="rounded-md border mt-2"
              />
            </div>

            {selectedDate && (
              <div>
                <Label>Available Time Slots for {DAYS[getDay(selectedDate)]}</Label>
                {availableSlots.length === 0 ? (
                  <div className="mt-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    <Clock className="inline h-4 w-4 mr-2" />
                    No available slots for this day. Please select another date.
                  </div>
                ) : (
                  <Select
                    value={bookingData.time_slot}
                    onValueChange={(value) => setBookingData({ ...bookingData, time_slot: value })}
                  >
                    <SelectTrigger data-testid="booking-time-select" className="mt-1.5">
                      <SelectValue placeholder="Select a time slot" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSlots.map((slot) => (
                        <SelectItem key={slot.id} value={`${slot.start_time} - ${slot.end_time}`}>
                          {slot.start_time} - {slot.end_time} (Max: {slot.max_appointments} appointments)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div>
              <Label>Notes (Optional)</Label>
              <Input
                data-testid="booking-notes-input"
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                placeholder="Any specific notes..."
                className="mt-1.5"
              />
            </div>

            <Button 
              data-testid="confirm-booking-btn" 
              type="submit" 
              className="w-full rounded-full bg-primary hover:bg-primary-hover"
              disabled={!selectedDate || !bookingData.time_slot}
            >
              Confirm Booking
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}